import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { modalStyles } from '../styles/modalStyles';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS, SOCKET_KEYS } from '../helpers/constants';
import { capitalizeFirstLetter } from '../helpers/stringUtils';
import socketService from '../services/socketService';
import { LanguageConstants } from '@/helpers/LanguageConstants';
import BusinessRulesSection from './ChannelSettings/BusinessRulesSection';
import type { BusinessRulesValidationResult } from '../services/businessRulesService';

// Types for channel data
interface Channel {
  id: string;
  name: string;
  description: string;
  channel_business_rules?: string;
  workspace_id: string;
  workspace_name: string;
  company_id: string;
  is_active: boolean;
  is_public: boolean;
  ai_enabled: boolean;
  channel_settings: {
    auto_summarize: boolean;
    notification_preferences: {
      email: boolean;
      push: boolean;
    };
  };
  message_count: number;
  last_message_at: string | null;
  total_apps_connected: number;
  total_users: number;
  total_datasources: number;
  app: {
    app_name: string;
    icon_url: string;
  }[];
  datasource: {
    datasource_name: string;
    file_name: string;
    file_type: string;
  }[];
  agent_count: number;
  agent_names: Record<string, any>;
  workflow_count: number;
  RCA: string;
  created_at: string;
  updated_at: string;
}

interface EditSpaceModalProps {
  open: boolean;
  onClose: () => void;
  channel: Channel | null;
  onSpaceUpdated?: () => void;
  /** Opt-in only — omit (as GroupsPage.tsx does) to keep the original plain Business Rules
   * textarea unchanged. Pass 'message' (as EmailConfigTab.tsx does for its message-channel
   * "Channel Config" edit) to enable description-driven business-rules suggestions +
   * validation, matching the same BusinessRulesSection flow already used for email channels. */
  channelType?: 'email' | 'message';
  isChannelAdmin?: boolean;
  /** Forwarded straight through to BusinessRulesSection's "See workflow canvas" button (only
   * rendered/enabled when channelType === 'message', matching the same validation-gated flow
   * EmailConfigTab.tsx already uses for email channels). Omit to keep this modal's original
   * behavior (GroupsPage.tsx's plain-textarea usage never passes this). Closes this modal first
   * so the caller's Business Rules Workflow tab underneath is actually visible. */
  onBuildWorkflow?: (rulesText: string) => void;
}



const EditSpaceModal: React.FC<EditSpaceModalProps> = ({
  open,
  onClose,
  channel,
  onSpaceUpdated,
  channelType,
  isChannelAdmin = true,
  onBuildWorkflow,
}) => {
  const [spaceData, setSpaceData] = useState({
    name: '',
    description: ''
  });
  const [businessRules, setBusinessRules] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Business-rules suggestion/validation state — only exercised when channelType === 'message'
  // (see BusinessRulesSection usage below); harmless/unused otherwise.
  const [businessRulesFetchNonce, setBusinessRulesFetchNonce] = useState(0);
  const [businessRulesValidationStatus, setBusinessRulesValidationStatus] = useState<
    'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean'
  >('idle');
  const [businessRulesForceSave, setBusinessRulesForceSave] = useState(false);
  const [businessRulesValidationResult, setBusinessRulesValidationResult] =
    useState<BusinessRulesValidationResult | null>(null);
  const [serverValidationResult, setServerValidationResult] =
    useState<BusinessRulesValidationResult | null>(null);

  const handleBusinessRulesValidationMetaChange = useCallback((meta: {
    status: 'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean';
    canUpdate: boolean;
    forceSave: boolean;
    result: BusinessRulesValidationResult | null;
  }) => {
    setBusinessRulesValidationStatus(meta.status);
    setBusinessRulesForceSave(meta.forceSave);
    setBusinessRulesValidationResult(meta.result);
    if (meta.status !== 'errors') {
      setServerValidationResult(null);
    }
  }, []);

  // Update form data when channel changes
  useEffect(() => {
    if (channel) {
      setSpaceData({
        name: channel.name,
        description: channel.description
      });
      setBusinessRules(channel.channel_business_rules || '');
      setBusinessRulesFetchNonce(0);
      setBusinessRulesValidationStatus('idle');
      setBusinessRulesForceSave(false);
      setBusinessRulesValidationResult(null);
      setServerValidationResult(null);
      // The description-watcher effect below fires on this same load — arm the skip so it
      // doesn't treat the channel's existing description as a user edit.
      skipNextDescriptionEffectRef.current = true;
    }
  }, [channel]);

  // Auto-refresh business-rules suggestions when the admin edits the Description — but not
  // on initial load (skipNextDescriptionEffectRef, armed above whenever `channel` changes),
  // so opening the dialog never wipes already-saved rules the moment it appears. Debounced
  // (500ms, same as this codebase's other description-triggered fetch in this file's
  // sibling EmailConfigTab.tsx) so a fetch fires once typing pauses, not on every keystroke.
  const skipNextDescriptionEffectRef = useRef(true);
  useEffect(() => {
    if (channelType !== 'message') return;
    if (skipNextDescriptionEffectRef.current) {
      skipNextDescriptionEffectRef.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      setBusinessRulesFetchNonce((n) => n + 1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [spaceData.description, channelType]);

  const handleInputChange = (field: string, value: string) => {
    setSpaceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClose = () => {
    setSpaceData({ name: '', description: '' });
    setBusinessRules('');
    setError(null);
    setBusinessRulesFetchNonce(0);
    setBusinessRulesValidationStatus('idle');
    setBusinessRulesForceSave(false);
    setBusinessRulesValidationResult(null);
    setServerValidationResult(null);
    onClose();
  };

  const handleUpdate = async () => {
    if (!channel) {
      setError('No channel selected for editing');
      return;
    }

    if (!spaceData.name.trim() || !spaceData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(channel.id)}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          name: spaceData.name.trim(),
          channel_description: spaceData.description.trim(),
          ...(businessRules.trim() ? { channel_business_rules: businessRules.trim() } : {}),
          // Only sent for the message-channel BusinessRulesSection flow (channelType === 'message')
          // — GroupsPage.tsx's plain-textarea usage never populates these, so its payload is unchanged.
          ...(channelType === 'message' && businessRules.trim() && businessRulesValidationResult
            ? { validation_result: businessRulesValidationResult }
            : {}),
          ...(channelType === 'message' && businessRules.trim() && businessRulesForceSave
            ? { force_save: true }
            : {}),
        })
      });

      if (!response.ok) {
        if (channelType === 'message') {
          const errorData = await response.json().catch(() => ({}));
          const serverResult = (errorData?.validation_result ||
            errorData?.detail?.validation_result) as BusinessRulesValidationResult | undefined;
          if (response.status === 422 && serverResult) {
            setServerValidationResult(serverResult);
            setBusinessRulesValidationResult(serverResult);
            setBusinessRulesValidationStatus(serverResult.status);
            setBusinessRulesForceSave(serverResult.status === 'warnings');
            setError('Business rules validation failed. Please review and try again.');
            return;
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedChannel = await response.json();
      
      // Emit socket event for channel updated
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (loggedInUserData) {
        try {
          const userData = JSON.parse(loggedInUserData);
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_UPDATED,
            data: {
              emailId: userData.email_id,
              channelId: channel.id
            }
          };
          
          await socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Note: We don't fail the channel update if socket fails
          // The channel is still updated successfully
        }
      }
      
      // Close modal and refresh the channel list
      handleClose();
      if (onSpaceUpdated) {
        onSpaceUpdated();
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update channel');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = spaceData.name.trim() && spaceData.description.trim();

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return; // Prevent closing when clicking outside
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: modalStyles.dialog
      }}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        <Typography variant="h5" component="div">
          Edit
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label={`${LanguageConstants.topic}`}
          value={spaceData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          margin="normal"
          required
          disabled
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Description"
          value={spaceData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          margin="normal"
          required
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />

        {channelType === 'message' && channel ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5 }}>
              Business Rules
            </Typography>
            <BusinessRulesSection
              variant="edit"
              saveMode="deferred"
              suggestTrigger="open"
              channelType="message"
              channelId={channel.id}
              workspaceName={channel.workspace_name || ''}
              channelDescription={spaceData.description}
              initialBusinessRules={channel.channel_business_rules || ''}
              value={businessRules}
              onRulesChange={setBusinessRules}
              isChannelAdmin={isChannelAdmin}
              externalValidationResult={serverValidationResult}
              onValidationMetaChange={handleBusinessRulesValidationMetaChange}
              descriptionSelectionNonce={businessRulesFetchNonce}
              onBuildWorkflow={
                onBuildWorkflow
                  ? (rulesText) => {
                      // Close first — this modal is a Dialog overlaid on the Settings drawer, so
                      // the Business Rules Workflow tab underneath is invisible until it's gone.
                      handleClose();
                      onBuildWorkflow(rulesText);
                    }
                  : undefined
              }
            />
          </Box>
        ) : (
          <TextField
            fullWidth
            label="Business Rules"
            value={businessRules}
            onChange={(e) => setBusinessRules(e.target.value)}
            margin="normal"
            multiline
            rows={4}
            placeholder="Enter business rules for this channel (optional)"
            inputProps={{ maxLength: 8000 }}
            sx={{ mb: 2 }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          disabled={!isFormValid || loading}
          variant="contained"
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            'Update'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSpaceModal; 